import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef, ViewChild } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';
import { BuilderScreenBetaService, JsonSyncConfiguration, JsonApplyResult, JsonValidationError } from '../services/builder-screen.service';
import { Observable, map, takeUntil, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { JsonValidationResult, JsonEditorBetaComponent } from '../json-editor/json-editor.component';

@Component({
  selector: 'app-builder-json-preview-beta',
  templateUrl: './json-preview.component.html',
  styleUrls: ['./json-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class JsonPreviewBetaComponent implements OnChanges, OnDestroy {
  @Input() jsonText: string | null = '{\n  /* JSON preview placeholder */\n}';
  @ViewChild('jsonEditor') jsonEditor?: JsonEditorBetaComponent;
  
  // Funcionalidade de colapso existente
  public collapsed = false;
  
  // Propriedades de validação aprimoradas
  public isValid = true;
  public validationError = '';
  
  // JSON com destaque de sintaxe
  public highlightedJson = '';
  
  // Informações de tipo de ação
  public actionTypes$: Observable<string[]>;

  // Modo de edição
  public isEditMode = false;
  public editableJsonData: any = {};
  public realTimeSync = false; // Controla se aplica mudanças em tempo real
  
  // Configuração de sincronização
  public syncConfig: JsonSyncConfiguration = {
    autoApply: false,
    debounceTime: 500,
    validateSchema: true,
    preserveSelection: true
  };

  // Status de aplicação
  public applyStatus: 'idle' | 'validating' | 'applying' | 'success' | 'error' = 'idle';
  public lastApplyResult: JsonApplyResult | null = null;
  
  // Subject para cleanup e debounce
  private destroy$ = new Subject<void>();
  private applyJsonDebounced$ = new Subject<any>();

  constructor(
    private readonly message: NzMessageService, 
    private readonly translate: TranslateService,
    private readonly builderService: BuilderScreenBetaService,
    private readonly cdr: ChangeDetectorRef
  ) {
    // Obtém tipos de ação dos componentes de botão
    this.actionTypes$ = this.builderService.nodes$.pipe(
      map(nodes => {
        const buttonNodes = nodes.filter(n => n.type === 'button');
        const actionTypes = buttonNodes
          .map(n => (n.props as any)?.actionType)
          .filter(type => type && type !== 'custom')
          .filter((type, index, arr) => arr.indexOf(type) === index); // valores únicos
        return actionTypes;
      })
    );

    // Configurar aplicação com debounce para modo tempo real
    this.setupRealTimeSync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jsonText']) {
      this.validateJson();
      this.highlightJson();
    }
  }

  // Copia JSON para área de transferência com feedback
  copy(): void {
    const text = this.jsonText ?? '';
    navigator.clipboard.writeText(text).then(() => {
      this.translate.get('builderScreen.json.copied').subscribe(msg => this.message.success(msg));
    }).catch(() => this.translate.get('builderScreen.json.copyFailed').subscribe(msg => this.message.error(msg)));
  }

  toggle(): void {
    this.collapsed = !this.collapsed;
  }

  // Funcionalidade aprimorada de exportação para arquivo
  exportJson(): void {
    const text = this.jsonText ?? '';
    try {
      // Valida JSON antes da exportação
      JSON.parse(text);
      
      const blob = new Blob([text], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `builder-config-${Date.now()}.json`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      this.translate.get('builderScreen.jsonPreview.exportSuccess').subscribe(msg => this.message.success(msg));
    } catch (error) {
      this.translate.get('builderScreen.jsonPreview.error').subscribe(msg => this.message.error(msg));
    }
  }

  // Método de validação aprimorado
  validateJson(): void {
    try {
      const text = this.jsonText ?? '';
      if (text.trim()) {
        JSON.parse(text);
        this.isValid = true;
        this.validationError = '';
      } else {
        this.isValid = true;
        this.validationError = '';
      }
    } catch (error) {
      this.isValid = false;
      this.validationError = error instanceof Error ? error.message : 'Invalid JSON';
    }
    this.cdr.markForCheck();
  }

  // Método de destaque de sintaxe
  highlightJson(): void {
    const text = this.jsonText ?? '';
    if (!text.trim()) {
      this.highlightedJson = '';
      return;
    }

    try {
      // Analisa e formata JSON com indentação adequada
      const parsed = JSON.parse(text);
      const formatted = JSON.stringify(parsed, null, 2);
      
      // Aplica destaque de sintaxe
      this.highlightedJson = this.applySyntaxHighlighting(formatted);
    } catch (error) {
      // Se JSON for inválido, apenas escapa HTML e mostra como está
      this.highlightedJson = this.escapeHtml(text);
    }
    
    this.cdr.markForCheck();
  }

  private applySyntaxHighlighting(json: string): string {
    return json
      .replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, (match) => {
        let cls = 'json-number';
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'json-key';
          } else {
            cls = 'json-string';
          }
        } else if (/true|false/.test(match)) {
          cls = 'json-boolean';
        } else if (/null/.test(match)) {
          cls = 'json-null';
        }
        return `<span class="${cls}">${match}</span>`;
      })
      .replace(/([{}[\],])/g, '<span class="json-punctuation">$1</span>');
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Preparação de salvamento no servidor (placeholder para integração futura com servidor)
  saveToServer(): void {
    const text = this.jsonText ?? '';
    try {
      JSON.parse(text);
      // TODO: Implementar lógica de salvamento no servidor aqui
      this.translate.get('builderScreen.jsonPreview.saveSuccess').subscribe(msg => this.message.info(msg));
    } catch (error) {
      this.translate.get('builderScreen.jsonPreview.error').subscribe(msg => this.message.error(msg));
    }
  }

  // Métodos auxiliares para exibição de tipo de ação
  getActionTypeColor(actionType: string): string {
    switch (actionType) {
      case 'save':
        return 'green';
      case 'cancel':
        return 'orange';
      default:
        return 'blue';
    }
  }

  getActionTypeIcon(actionType: string): string {
    switch (actionType) {
      case 'save':
        return 'save';
      case 'cancel':
        return 'close';
      default:
        return 'setting';
    }
  }

  // Obtém informações de tamanho do JSON
  getJsonSize(): number {
    const text = this.jsonText ?? '';
    return text.split('\n').length;
  }

  // Métodos para modo de edição
  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    
    if (this.isEditMode) {
      // Obtém o JSON atual do builder service
      this.builderService.jsonOutput$.pipe(
        takeUntil(this.destroy$)
      ).subscribe(currentJsonOutput => {
        try {
          // Usa o JSON output atual do builder
          this.editableJsonData = currentJsonOutput || {};
          console.log('JsonPreview: Loaded current JSON for editing:', this.editableJsonData);
          
          // Força atualização do editor se já estiver inicializado
          if (this.jsonEditor) {
            setTimeout(() => {
              this.jsonEditor?.forceUpdate(this.editableJsonData);
            }, 100);
          }
          
          this.cdr.markForCheck();
        } catch (error) {
          this.editableJsonData = {};
          this.message.warning('Erro ao carregar JSON atual, iniciando com objeto vazio');
          this.cdr.markForCheck();
        }
      });
    }
    
    this.cdr.markForCheck();
  }

  // Callback quando JSON é editado no editor - Task 2.1 e 2.2 implementação
  onJsonEditorChange(newJsonData: any): void {
    // Armazena os dados editados
    this.editableJsonData = newJsonData;
    
    // Se modo tempo real estiver ativado, usa debounce para aplicar mudanças
    if (this.realTimeSync && this.isValid) {
      this.applyJsonDebounced$.next(newJsonData);
    }
  }

  // Callback para validação do editor
  onJsonValidationChange(validation: JsonValidationResult): void {
    this.isValid = validation.isValid;
    this.validationError = validation.error || '';
    this.cdr.markForCheck();
  }

  // Aplica JSON editado ao canvas usando novo método de sincronização
  private applyJsonToCanvas(jsonData: any): JsonApplyResult | null {
    try {
      if (!jsonData || typeof jsonData !== 'object') {
        console.warn('Invalid JSON data for canvas application');
        return null;
      }

      console.log('Applying JSON to canvas:', jsonData);
      
      // Usar novo método de aplicação JSON
      const result = this.builderService.applyJsonConfiguration(jsonData);
      this.lastApplyResult = result;
      
      if (result.success) {
        console.log(`Successfully applied JSON: ${result.appliedNodes} nodes created`);
        
        // Atualiza o texto JSON exibido apenas se não estivermos em modo de edição
        if (!this.isEditMode) {
          this.jsonText = JSON.stringify(jsonData, null, 2);
          this.highlightJson();
        }
        
        // Mostrar warnings se existirem
        if (result.warnings && result.warnings.length > 0) {
          result.warnings.forEach(warning => {
            this.message.warning(warning);
          });
        }
      } else {
        console.error('Failed to apply JSON:', result.errors);
        this.showApplyErrors(result.errors);
      }
      
      return result;
    } catch (error) {
      console.error('Erro ao aplicar JSON ao canvas:', error);
      this.message.error('Erro ao aplicar JSON ao canvas');
      return null;
    }
  }

  // Salva alterações e sai do modo de edição - Task 2.1 implementação
  saveJsonChanges(): void {
    if (!this.jsonEditor || !this.isValid) {
      this.message.error('Editor não está pronto ou JSON inválido');
      return;
    }

    this.applyStatus = 'validating';
    this.cdr.markForCheck();

    try {
      // Obtém o JSON atual do editor
      const currentEditorJson = this.jsonEditor.getEditorContent();
      console.log('Saving JSON from editor:', currentEditorJson);
      
      if (!currentEditorJson) {
        this.applyStatus = 'error';
        this.message.error('Não foi possível obter o conteúdo do editor');
        this.cdr.markForCheck();
        return;
      }

      // Parse do JSON para validar
      let parsedJson;
      try {
        parsedJson = JSON.parse(currentEditorJson);
        console.log('Parsed JSON for saving:', parsedJson);
      } catch (parseError) {
        this.applyStatus = 'error';
        this.message.error('JSON inválido. Corrija os erros antes de salvar.');
        this.cdr.markForCheck();
        return;
      }

      this.applyStatus = 'applying';
      this.cdr.markForCheck();

      // Aplicar via novo método com feedback detalhado
      const result = this.applyJsonToCanvas(parsedJson);
      
      if (result && result.success) {
        this.applyStatus = 'success';
        this.isEditMode = false;
        
        this.translate.get('builderScreen.jsonPreview.applied', { 
          nodes: result.appliedNodes 
        }).subscribe(msg => this.message.success(msg));
      } else {
        this.applyStatus = 'error';
        // Errors já foram mostrados no método applyJsonToCanvas
      }

    } catch (error) {
      this.applyStatus = 'error';
      this.translate.get('builderScreen.jsonPreview.error')
        .subscribe(msg => this.message.error(msg));
      console.error('Erro ao salvar JSON:', error);
    }

    this.cdr.markForCheck();
  }

  // Cancela alterações e sai do modo de edição
  cancelJsonChanges(): void {
    // Se estava em modo tempo real, pode ter aplicado mudanças
    // Vamos recarregar o estado original
    if (this.realTimeSync) {
      // Recarrega o JSON atual do builder para desfazer mudanças em tempo real
      this.builderService.jsonOutput$.pipe(
        takeUntil(this.destroy$)
      ).subscribe(currentJsonOutput => {
        this.editableJsonData = currentJsonOutput || {};
      });
    }
    
    this.isEditMode = false;
    this.realTimeSync = false; // Reseta para modo manual
    this.cdr.markForCheck();
  }

  // Callback quando o editor estiver pronto
  onEditorReady(editor: any): void {
    console.log('JsonPreview: Editor ready, loading current data');
    // Força atualização com dados atuais quando editor estiver pronto
    if (this.editableJsonData && this.jsonEditor) {
      setTimeout(() => {
        this.jsonEditor?.forceUpdate(this.editableJsonData);
      }, 50);
    }
  }

  // Carrega JSON de exemplo para teste
  loadSampleJson(): void {
    const sampleJson = {
      "tabs": [
        {
          "id": "tab1",
          "name": "Dados Gerais",
          "icon": "user",
          "disabled": false
        }
      ],
      "panels": [
        {
          "id": "panel1",
          "name": "Informações Básicas",
          "tab": "tab1",
          "active": true,
          "disabled": false
        }
      ],
      "fields": [
        {
          "id": "field1",
          "type": "text",
          "field": "nome",
          "label": "Nome",
          "placeholder": "Digite o nome",
          "panel": "panel1",
          "row": 0,
          "col": 0,
          "colSpan": 12
        },
        {
          "id": "field2", 
          "type": "text",
          "field": "email",
          "label": "Email",
          "placeholder": "Digite o email",
          "panel": "panel1",
          "row": 0,
          "col": 12,
          "colSpan": 12
        },
        {
          "id": "field3",
          "type": "switch",
          "field": "ativo",
          "label": "Usuário Ativo",
          "panel": "panel1",
          "row": 1,
          "col": 0,
          "colSpan": 24
        }
      ]
    };
    
    this.editableJsonData = sampleJson;
    
    // Força atualização do editor
    if (this.jsonEditor) {
      setTimeout(() => {
        this.jsonEditor?.forceUpdate(this.editableJsonData);
      }, 50);
    }
    
    this.cdr.markForCheck();
  }

  // ========== Novos métodos para JSON Preview Fix ==========

  /**
   * Configura aplicação com debounce para modo tempo real
   * Task 2.2: Implementação de debounce inteligente
   */
  private setupRealTimeSync(): void {
    this.applyJsonDebounced$.pipe(
      debounceTime(this.syncConfig.debounceTime),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(jsonData => {
      if (this.syncConfig.validateSchema) {
        // Validar antes de aplicar
        const validation = this.builderService.validateJsonConfiguration(jsonData);
        if (!validation.isValid) {
          console.warn('Real-time sync: Invalid JSON, skipping application', validation.errors);
          return;
        }
      }
      
      console.log('Real-time sync: Applying JSON with debounce');
      const result = this.applyJsonToCanvas(jsonData);
      
      if (!result || !result.success) {
        // Desabilitar tempo real em caso de erro para evitar loops
        this.realTimeSync = false;
        this.message.warning('Modo tempo real desabilitado devido a erro de aplicação');
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Exibe erros de aplicação JSON de forma organizada
   * Task 2.3: Feedback visual para erros
   */
  private showApplyErrors(errors: JsonValidationError[]): void {
    if (!errors || errors.length === 0) return;

    // Agrupar erros por tipo
    const syntaxErrors = errors.filter(e => e.type === 'syntax');
    const schemaErrors = errors.filter(e => e.type === 'schema');
    const componentErrors = errors.filter(e => e.type === 'component');

    // Mostrar erros de sintaxe primeiro
    syntaxErrors.forEach(error => {
      let message = `Erro de sintaxe: ${error.message}`;
      if (error.line && error.column) {
        message += ` (Linha ${error.line}, Coluna ${error.column})`;
      }
      this.message.error(message);
    });

    // Mostrar erros de schema
    schemaErrors.forEach(error => {
      this.message.error(`Erro de esquema: ${error.message}`);
    });

    // Mostrar erros de componente
    componentErrors.forEach(error => {
      let message = `Erro de componente: ${error.message}`;
      if (error.componentType) {
        message += ` (${error.componentType})`;
      }
      this.message.error(message);
    });
  }

  /**
   * Limpa resultado de aplicação anterior
   */
  public clearApplyResult(): void {
    this.lastApplyResult = null;
    this.applyStatus = 'idle';
    this.cdr.markForCheck();
  }

  /**
   * Obtém descrição de erro de aplicação para exibição
   */
  public getApplyErrorDescription(): string {
    if (!this.lastApplyResult || !this.lastApplyResult.errors) return '';
    
    return this.lastApplyResult.errors
      .map(error => error.message)
      .join('; ');
  }

  /**
   * Métodos auxiliares para status de sincronização - Task 2.3
   */
  public getSyncStatusColor(): string {
    switch (this.applyStatus) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'validating':
      case 'applying': return 'processing';
      default: return 'default';
    }
  }

  public getSyncStatusIcon(): string {
    switch (this.applyStatus) {
      case 'success': return 'check-circle';
      case 'error': return 'close-circle';
      case 'validating': return 'loading';
      case 'applying': return 'sync';
      default: return 'info-circle';
    }
  }

  public getSyncStatusText(): string {
    switch (this.applyStatus) {
      case 'idle': return 'builderScreen.jsonPreview.syncStatus.idle';
      case 'validating': return 'builderScreen.jsonPreview.syncStatus.validating';
      case 'applying': return 'builderScreen.jsonPreview.syncStatus.applying';
      case 'success': return 'builderScreen.jsonPreview.syncStatus.success';
      case 'error': return 'builderScreen.jsonPreview.syncStatus.error';
      default: return 'builderScreen.jsonPreview.syncStatus.idle';
    }
  }

  // Limpa recursos quando componente é destruído
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}


